import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  Table,
  Loader,
  Message,
  Icon,
  Input,
  Dropdown,
  Form,
  Confirm,
  Popup,
} from "semantic-ui-react";
import { documentApi } from "@api/documents";
import {
  marcTagDescriptions,
  subfieldDescriptions,
  indicatorInfo,
} from "./marc21Metadata";

const MARC21Preview = ({ documentId, trigger, onSave, isEditMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editedFields, setEditedFields] = useState([]);
  const [error, setError] = useState(null);
  const [addFieldModalOpen, setAddFieldModalOpen] = useState(false);
  const [lookupModalOpen, setLookupModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [newFieldTag, setNewFieldTag] = useState("");
  const [newFieldInd1, setNewFieldInd1] = useState("");
  const [newFieldInd2, setNewFieldInd2] = useState("");
  const [newFieldSubfields, setNewFieldSubfields] = useState([
    { code: "a", value: "" },
  ]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const getIndicatorDescription = (tag, position, value) => {
    if (value === " " || value === "") return "Blank";
    const fieldInfo = indicatorInfo[tag];
    if (fieldInfo) {
      const posInfo = position === 1 ? fieldInfo.ind1 : fieldInfo.ind2;
      if (posInfo && posInfo[value]) {
        return posInfo[value];
      }
    }
    return `Value: ${value}`;
  };

  const fetchMARC21Preview = async () => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${documentApi.searchBaseURL}/${documentId}?format=marcxml`,
        {
          credentials: "include",
          headers: {
            Accept: "application/marcxml+xml",
          },
          cache: "default",
        }
      );
      if (!response.ok && response.status !== 304) {
        throw new Error(`Failed to fetch MARC21 data: ${response.status}`);
      }
      const xmlText = await response.text();
      const fields = parseMARC21Fields(xmlText);
      setEditedFields(fields);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchMARC21Preview();
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditedFields([]);
    setError(null);
    setSelectedTag("");
  };

  const handleSave = () => {
    if (onSave && editedFields.length > 0) {
      onSave(editedFields);
    }
    handleClose();
  };

  const parseMARC21Fields = (xml) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, "text/xml");
      const fields = [];
      const leader = doc.querySelector("leader");
      if (leader) {
        fields.push({
          tag: "LDR",
          ind1: "",
          ind2: "",
          subfields: [{ code: "", value: leader.textContent }],
        });
      }
      const controlFields = doc.querySelectorAll("controlfield");
      controlFields.forEach((field) => {
        fields.push({
          tag: field.getAttribute("tag"),
          ind1: "",
          ind2: "",
          subfields: [{ code: "", value: field.textContent }],
        });
      });
      const dataFields = doc.querySelectorAll("datafield");
      dataFields.forEach((field) => {
        const subfields = [];
        const subfieldElements = field.querySelectorAll("subfield");
        subfieldElements.forEach((sf) => {
          subfields.push({
            code: sf.getAttribute("code"),
            value: sf.textContent,
          });
        });
        fields.push({
          tag: field.getAttribute("tag"),
          ind1: field.getAttribute("ind1") || " ",
          ind2: field.getAttribute("ind2") || " ",
          subfields: subfields,
        });
      });
      return fields;
    } catch (e) {
      console.error("Error parsing MARC21 fields:", e);
      return [];
    }
  };

  const handleOpenAddFieldModal = () => {
    setEditingIndex(null);
    setNewFieldTag("");
    setNewFieldInd1(" ");
    setNewFieldInd2(" ");
    setNewFieldSubfields([{ code: "a", value: "" }]);
    setAddFieldModalOpen(true);
  };

  const handleEditField = (index) => {
    const field = editedFields[index];
    setEditingIndex(index);
    setNewFieldTag(field.tag);
    setNewFieldInd1(field.ind1 || " ");
    setNewFieldInd2(field.ind2 || " ");
    setNewFieldSubfields([...field.subfields]);
    setAddFieldModalOpen(true);
  };

  const handleSaveField = () => {
    if (!newFieldTag || newFieldTag.trim() === "") {
      alert("Tag is required");
      return;
    }
    const validSubfields = newFieldSubfields.filter(
      (sf) => sf.value.trim() !== ""
    );
    if (validSubfields.length === 0) {
      alert("At least one subfield with content is required");
      return;
    }
    const newField = {
      tag: newFieldTag.trim(),
      ind1: newFieldInd1 || " ",
      ind2: newFieldInd2 || " ",
      subfields: validSubfields,
    };
    if (editingIndex !== null) {
      const updated = [...editedFields];
      updated[editingIndex] = newField;
      setEditedFields(updated);
    } else {
      setEditedFields([...editedFields, newField]);
    }
    setAddFieldModalOpen(false);
  };

  const handleDeleteField = (index) => {
    setDeleteIndex(index);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      const updated = editedFields.filter((_, i) => i !== deleteIndex);
      setEditedFields(updated);
    }
    setDeleteConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleLookupTag = () => {
    setLookupModalOpen(true);
  };

  const handleSelectTagFromLookup = (tag) => {
    setNewFieldTag(tag);
    setLookupModalOpen(false);
  };

  const addSubfield = () => {
    setNewFieldSubfields([...newFieldSubfields, { code: "a", value: "" }]);
  };

  const updateSubfield = (index, field, value) => {
    const updated = [...newFieldSubfields];
    updated[index][field] = value;
    setNewFieldSubfields(updated);
  };

  const removeSubfield = (index) => {
    if (newFieldSubfields.length > 1) {
      setNewFieldSubfields(newFieldSubfields.filter((_, i) => i !== index));
    }
  };

  const filteredFields = useMemo(() => {
    if (!selectedTag) return editedFields;
    return editedFields.filter((field) => field.tag === selectedTag);
  }, [editedFields, selectedTag]);

  const tagOptions = Object.keys(marcTagDescriptions).map((tag) => ({
    key: tag,
    text: `${tag} - ${marcTagDescriptions[tag]}`,
    value: tag,
  }));

  const sortedMarcTags = Object.entries(marcTagDescriptions).sort(
    ([a], [b]) => {
      // Pad numeric tags to 3 digits, keep LDR at the top
      const pad = (tag) => {
        if (tag === "LDR") return "000";
        return tag.padStart(3, "0");
      };
      return pad(a).localeCompare(pad(b));
    }
  );

  return (
    <>
      <span onClick={handleOpen} style={{ cursor: "pointer" }}>
        {trigger}
      </span>

      <Modal open={isOpen} onClose={handleClose} size="fullscreen">
        <Modal.Header>
          <Icon name="file code outline" />
          MARC21 Record Preview
        </Modal.Header>
        <Modal.Content scrolling>
          {loading && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Loader active inline="centered" />
              <p>Loading MARC21 data...</p>
            </div>
          )}
          {error && (
            <Message error>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}
          {!loading && !error && editedFields.length > 0 && (
            <>
              {isEditMode && (
                <div
                  style={{
                    marginBottom: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <Button primary onClick={handleOpenAddFieldModal}>
                      <Icon name="plus" /> Add Field
                    </Button>
                    <Dropdown
                      placeholder="Filter by Tag"
                      selection
                      clearable
                      options={tagOptions}
                      value={selectedTag}
                      onChange={(e, { value }) => setSelectedTag(value || "")}
                      style={{ minWidth: "250px" }}
                    />
                    {selectedTag && (
                      <span style={{ color: "#666", fontSize: "0.9em" }}>
                        Showing {filteredFields.length} of {editedFields.length}{" "}
                        fields
                      </span>
                    )}
                  </div>
                </div>
              )}
              <Table celled striped compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={1}>Tag</Table.HeaderCell>
                    <Table.HeaderCell width={1}>Ind1</Table.HeaderCell>
                    <Table.HeaderCell width={1}>Ind2</Table.HeaderCell>
                    <Table.HeaderCell width={1}>Code</Table.HeaderCell>
                    <Table.HeaderCell width={10}>Data</Table.HeaderCell>
                    {isEditMode && (
                      <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredFields.map((field, fieldIndex) => {
                    const actualIndex = editedFields.indexOf(field);
                    return field.subfields.map((subfield, subfieldIndex) => (
                      <Table.Row key={`${actualIndex}-${subfieldIndex}`}>
                        {subfieldIndex === 0 ? (
                          <>
                            <Table.Cell rowSpan={field.subfields.length}>
                              <Popup
                                content={
                                  <div>
                                    <strong>Tag {field.tag}</strong>
                                    <br />
                                    {marcTagDescriptions[field.tag] ||
                                      "No description available"}
                                  </div>
                                }
                                trigger={
                                  <span
                                    style={{
                                      cursor: "help",
                                      borderBottom: "1px dotted #999",
                                    }}
                                  >
                                    <strong>{field.tag}</strong>
                                  </span>
                                }
                                position="top left"
                                size="small"
                                inverted
                              />
                            </Table.Cell>
                            <Table.Cell rowSpan={field.subfields.length}>
                              <Popup
                                content={
                                  <div>
                                    <strong>Indicator 1</strong>
                                    <br />
                                    {getIndicatorDescription(
                                      field.tag,
                                      1,
                                      field.ind1
                                    )}
                                  </div>
                                }
                                trigger={
                                  <span style={{ cursor: "help" }}>
                                    {field.ind1 || " "}
                                  </span>
                                }
                                position="top center"
                                size="small"
                                inverted
                              />
                            </Table.Cell>
                            <Table.Cell rowSpan={field.subfields.length}>
                              <Popup
                                content={
                                  <div>
                                    <strong>Indicator 2</strong>
                                    <br />
                                    {getIndicatorDescription(
                                      field.tag,
                                      2,
                                      field.ind2
                                    )}
                                  </div>
                                }
                                trigger={
                                  <span style={{ cursor: "help" }}>
                                    {field.ind2 || " "}
                                  </span>
                                }
                                position="top center"
                                size="small"
                                inverted
                              />
                            </Table.Cell>
                          </>
                        ) : null}
                        <Table.Cell>
                          <Popup
                            content={
                              <div>
                                <strong>Subfield ${subfield.code}</strong>
                                <br />
                                {subfieldDescriptions[subfield.code] ||
                                  "Subfield code"}
                              </div>
                            }
                            trigger={
                              <span
                                style={{
                                  cursor: "help",
                                  borderBottom: "1px dotted #999",
                                }}
                              >
                                <strong>{subfield.code}</strong>
                              </span>
                            }
                            position="top left"
                            size="small"
                            inverted
                          />
                        </Table.Cell>
                        <Table.Cell>{subfield.value}</Table.Cell>
                        {isEditMode && subfieldIndex === 0 && (
                          <Table.Cell
                            rowSpan={field.subfields.length}
                            textAlign="center"
                          >
                            <Button.Group size="mini">
                              <Button
                                icon="edit"
                                onClick={() => handleEditField(actualIndex)}
                                title="Edit field"
                              />
                              <Button
                                icon="trash"
                                onClick={() => handleDeleteField(actualIndex)}
                                title="Delete field"
                                color="red"
                              />
                            </Button.Group>
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ));
                  })}
                </Table.Body>
              </Table>
            </>
          )}
          {!loading && !error && editedFields.length === 0 && (
            <Message warning>
              <Message.Header>No MARC21 Data</Message.Header>
              <p>No MARC21 record found for this document.</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleClose}>
            {isEditMode ? "Cancel" : "Close"}
          </Button>
          {isEditMode && (
            <Button
              primary
              onClick={handleSave}
              disabled={editedFields.length === 0}
            >
              <Icon name="save" />
              Save Changes to Document
            </Button>
          )}
        </Modal.Actions>
      </Modal>
      <Modal
        open={addFieldModalOpen}
        onClose={() => setAddFieldModalOpen(false)}
        size="small"
      >
        <Modal.Header style={{ backgroundColor: "#225745", color: "white" }}>
          {editingIndex !== null ? "Edit" : "Add"} MARC Field
          <Icon
            name="close"
            style={{ float: "right", cursor: "pointer" }}
            onClick={() => setAddFieldModalOpen(false)}
          />
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Tag *</label>
                <Input
                  value={newFieldTag}
                  onChange={(e) => setNewFieldTag(e.target.value)}
                  maxLength={3}
                  placeholder="e.g., 245"
                />
              </Form.Field>
              <Form.Field>
                <label>Indicator 1</label>
                <Input
                  value={newFieldInd1 === " " ? "" : newFieldInd1}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewFieldInd1(val === "" ? " " : val.slice(0, 1));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") setNewFieldInd1(" ");
                  }}
                  maxLength={1}
                />
              </Form.Field>
              <Form.Field>
                <label>Indicator 2</label>
                <Input
                  value={newFieldInd2 === " " ? "" : newFieldInd2}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewFieldInd2(val === "" ? " " : val.slice(0, 1));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") setNewFieldInd2(" ");
                  }}
                  maxLength={1}
                />
              </Form.Field>
            </Form.Group>
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "16px", // This adds vertical gap between rows
              }}
            >
              <label
                style={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "10px",
                }}
              >
                Subfields:
              </label>
              {newFieldSubfields.map((subfield, index) => (
                <Form.Group
                  key={index}
                  style={{
                    gap: "12px",
                    display: "flex",
                    marginTop: index === 0 ? "8px" : "0",
                  }}
                >
                  <Form.Field width={2}>
                    <Input
                      value={subfield.code}
                      onChange={(e) =>
                        updateSubfield(
                          index,
                          "code",
                          e.target.value.slice(0, 1)
                        )
                      }
                      maxLength={1}
                      placeholder="a"
                    />
                  </Form.Field>
                  <Form.Field width={12}>
                    <Input
                      value={subfield.value}
                      onChange={(e) =>
                        updateSubfield(index, "value", e.target.value)
                      }
                      placeholder="Subfield content"
                    />
                  </Form.Field>
                  <Form.Field width={2}>
                    <Button
                      icon="minus"
                      onClick={() => removeSubfield(index)}
                      disabled={newFieldSubfields.length === 1}
                      size="small"
                    />
                  </Form.Field>
                </Form.Group>
              ))}
              <Button
                icon="plus"
                content="Add Subfield"
                onClick={addSubfield}
                size="small"
                style={{ marginTop: "12px", width: "145px" }}
              />
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={handleSaveField}>
            <Icon name="save" /> Save Field
          </Button>
          <Button
            color="yellow"
            onClick={handleLookupTag}
            style={{ color: "black" }}
          >
            <Icon name="search" /> Lookup Tag
          </Button>
          <Button onClick={() => setAddFieldModalOpen(false)}>Cancel</Button>
        </Modal.Actions>
      </Modal>
      <Modal
        open={lookupModalOpen}
        onClose={() => setLookupModalOpen(false)}
        size="small"
      >
        <Modal.Header style={{ backgroundColor: "#f4b952", color: "white" }}>
          Lookup MARC Tag
          <Icon
            name="close"
            style={{ float: "right", cursor: "pointer" }}
            onClick={() => setLookupModalOpen(false)}
          />
        </Modal.Header>
        <Modal.Content scrolling style={{ maxHeight: "400px" }}>
          <Table celled striped selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Tag</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedMarcTags.map(([tag, description]) => (
                <Table.Row
                  key={tag}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSelectTagFromLookup(tag)}
                >
                  <Table.Cell>
                    <strong>{tag}</strong>
                  </Table.Cell>
                  <Table.Cell>{description}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setLookupModalOpen(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
      <Confirm
        open={deleteConfirmOpen}
        header="Delete MARC Field"
        content="Are you sure you want to delete this field? This action cannot be undone."
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeleteIndex(null);
        }}
        onConfirm={confirmDelete}
      />
    </>
  );
};

MARC21Preview.propTypes = {
  documentId: PropTypes.string.isRequired,
  trigger: PropTypes.node.isRequired,
  onSave: PropTypes.func,
  isEditMode: PropTypes.bool,
};

MARC21Preview.defaultProps = {
  onSave: null,
  isEditMode: false,
};

export default MARC21Preview;
